//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Button, Menu } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { DeploymentNotificationSummary } from "../../api/data/v1/idata";
import { OrganizationNotificationSummary } from "../../api/resourcemanager/v1/iresourcemanager";
import TabLayout from "../../components/Tab/Tab";
import { ITabConfig } from "../../components/Tab/TabTypes";
import { ContentSegment, ErrorMessage, SecondaryMenu } from "../../ui/lib";
import { Permission } from "../../util/PermissionCache";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import { ActiveNotificationsView, mergeSummaries } from "./ActiveNotificationsView";
import CreateNotificationRuleModal from "./CreateNotificationRuleModal";
import { RuleAuditLogView, loadRuleChanges } from "./RuleAuditLogView";
import { RulesView } from "./RulesView";
import {
  DerivedRuleChange,
  NotificationRule,
  NotificationRuleTarget,
  fromDeploymentRule,
  fromOrganizationRule,
  maxRulesPerTarget,
  toDeploymentRule,
  toOrganizationRule,
} from "./types";

type TargetErrors = Partial<Record<NotificationRuleTarget, string>>;

const errorText = (errors: TargetErrors) =>
  (Object.keys(errors) as NotificationRuleTarget[])
    .filter((k) => !!errors[k])
    .map((k) => `${k}: ${errors[k]}`)
    .join("; ");

export interface IPlatformNotificationsViewProps extends IWithRefreshProps, RouteComponentProps {
  hasPermission: (permission: Permission) => boolean;
}

// PlatformNotificationsView manages AMP-wide notification rules (stored by datad / rmd, applied by
// deployment-inspector and organization-inspector), shows the inventory of every notification currently
// present on the platform and the audit log of rule changes (changelogd).
const PlatformNotificationsView = (props: IPlatformNotificationsViewProps) => {
  const { hasPermission, subscribeUrl } = props;
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [rulesError, setRulesError] = useState<TargetErrors>({});
  const [deploymentSummaries, setDeploymentSummaries] = useState<DeploymentNotificationSummary[]>([]);
  const [organizationSummaries, setOrganizationSummaries] = useState<OrganizationNotificationSummary[]>([]);
  const [summariesError, setSummariesError] = useState<TargetErrors>({});
  const [changes, setChanges] = useState<DerivedRuleChange[]>([]);
  const [changesError, setChangesError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [modal, setModal] = useState<{ existing?: NotificationRule } | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(0);
  const [auditRuleFilter, setAuditRuleFilter] = useState<string | "all">("all");

  const canCreate: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deploymentnotificationrule.create"),
    organization: hasPermission("internal-dashboard.organizationnotificationrule.create"),
  };
  const canUpdate: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deploymentnotificationrule.update"),
    organization: hasPermission("internal-dashboard.organizationnotificationrule.update"),
  };
  const canDelete: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deploymentnotificationrule.delete"),
    organization: hasPermission("internal-dashboard.organizationnotificationrule.delete"),
  };
  const canListRules: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deploymentnotificationrule.list"),
    organization: hasPermission("internal-dashboard.organizationnotificationrule.list"),
  };
  const listableTargets = (Object.keys(canListRules) as NotificationRuleTarget[]).filter((t) => canListRules[t]);
  const canListSummaries: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deployment.list-notification-summaries"),
    organization: hasPermission("internal-dashboard.organization.list-notification-summaries"),
  };
  const showActive = canListSummaries.deployment || canListSummaries.organization;
  const canShowAffected: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deployment.list") && hasPermission("internal-dashboard.deployment.count"),
    organization: hasPermission("internal-dashboard.organization.list") && hasPermission("internal-dashboard.organization.count"),
  };
  const canRemoveEverywhere: Record<NotificationRuleTarget, boolean> = {
    deployment: hasPermission("internal-dashboard.deployment.remove-notification-from-all"),
    organization: hasPermission("internal-dashboard.organization.remove-notification-from-all"),
  };
  const creatableTargets = (Object.keys(canCreate) as NotificationRuleTarget[]).filter((t) => canCreate[t]);

  // Rules of a target whose list permission is not held are treated as empty (no call, no error).
  const loadRules = async () => {
    setRulesError({});
    const [d, o] = await Promise.allSettled([
      canListRules.deployment ? apiClients.idashboardClient.ListDeploymentNotificationRules({ page_size: maxRulesPerTarget }) : Promise.resolve({ items: [] }),
      canListRules.organization
        ? apiClients.idashboardClient.ListOrganizationNotificationRules({ page_size: maxRulesPerTarget })
        : Promise.resolve({ items: [] }),
    ]);
    const rules: NotificationRule[] = [];
    if (d.status === "fulfilled") rules.push(...(d.value.items || []).map(fromDeploymentRule));
    else setRulesError((e) => ({ ...e, deployment: String(d.reason) }));
    if (o.status === "fulfilled") rules.push(...(o.value.items || []).map(fromOrganizationRule));
    else setRulesError((e) => ({ ...e, organization: String(o.reason) }));
    setRules(rules.sort((a, b) => (b.created_at?.getTime() || 0) - (a.created_at?.getTime() || 0)));
  };

  // Summaries of a target whose permission is not held are treated as empty (no call, no error).
  const loadSummaries = async () => {
    setSummariesError({});
    const [d, o] = await Promise.allSettled([
      canListSummaries.deployment ? apiClients.idashboardClient.ListDeploymentNotificationSummaries({}) : Promise.resolve({ items: [] }),
      canListSummaries.organization ? apiClients.idashboardClient.ListOrganizationNotificationSummaries({}) : Promise.resolve({ items: [] }),
    ]);
    if (d.status === "fulfilled") setDeploymentSummaries(d.value.items || []);
    else setSummariesError((e) => ({ ...e, deployment: String(d.reason) }));
    if (o.status === "fulfilled") setOrganizationSummaries(o.value.items || []);
    else setSummariesError((e) => ({ ...e, organization: String(o.reason) }));
  };

  const loadChanges = async () => {
    const { changes, errors } = await loadRuleChanges(listableTargets);
    setChanges(changes);
    setChangesError(errors.length ? errors.join("; ") : undefined);
  };

  const reload = async () => {
    await Promise.all([loadRules(), loadSummaries(), loadChanges()]);
  };

  useEffect(() => {
    // subscribeUrl runs the callback once on subscription; only load directly when there is no subscription.
    if (subscribeUrl) {
      subscribeUrl(reload);
    } else {
      reload();
    }
  }, []);

  const mutate = async (fn: () => Promise<unknown>) => {
    try {
      setError(undefined);
      await fn();
    } catch (e) {
      setError(String(e));
    }
    await reload();
  };

  const toggleRule = (rule: NotificationRule, enabled: boolean) =>
    mutate(() =>
      rule.target === "organization"
        ? apiClients.idashboardClient.UpdateOrganizationNotificationRule(toOrganizationRule({ ...rule, enabled }))
        : apiClients.idashboardClient.UpdateDeploymentNotificationRule(toDeploymentRule({ ...rule, enabled }))
    );

  const deleteRule = (rule: NotificationRule) =>
    mutate(() =>
      rule.target === "organization"
        ? apiClients.idashboardClient.DeleteOrganizationNotificationRule({ id: rule.id })
        : apiClients.idashboardClient.DeleteDeploymentNotificationRule({ id: rule.id })
    );

  const removeEverywhere = (notificationId: string, kinds: NotificationRuleTarget[]) =>
    mutate(() =>
      Promise.all(
        kinds.map((kind) =>
          kind === "organization"
            ? apiClients.idashboardClient.RemoveOrganizationNotificationFromAll({ notification_id: notificationId })
            : apiClients.idashboardClient.RemoveDeploymentNotificationFromAll({ notification_id: notificationId })
        )
      )
    );

  // Tabs: Rules, [Active notifications], Audit log.
  const auditTabIndex = showActive ? 2 : 1;

  const showHistory = (rule: NotificationRule) => {
    setAuditRuleFilter(rule.url);
    setActiveTab(auditTabIndex);
  };

  const active = mergeSummaries(deploymentSummaries, organizationSummaries, rules);

  const tabConfig: ITabConfig[] = [
    {
      menuItem: { id: "rules", children: `Rules (${rules.length})` },
      content: () => (
        <RulesView
          rules={rules}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onToggle={toggleRule}
          onEdit={(rule) => setModal({ existing: rule })}
          onDelete={deleteRule}
          onShowHistory={showHistory}
        />
      ),
    },
    ...(showActive
      ? [
          {
            menuItem: { id: "active", children: `Active notifications (${active.length})` },
            content: () => (
              <ActiveNotificationsView
                {...props}
                items={active}
                canRemoveEverywhere={canRemoveEverywhere}
                canListRules={canListRules}
                canShowAffected={canShowAffected}
                rulesError={rulesError}
                onRemoveEverywhere={removeEverywhere}
              />
            ),
          },
        ]
      : []),
    {
      menuItem: { id: "audit", children: `Audit log (${changes.length})` },
      content: () => <RuleAuditLogView changes={changes} ruleFilter={auditRuleFilter} onRuleFilterChange={setAuditRuleFilter} />,
    },
  ];

  const loadErrors = [errorText(rulesError), errorText(summariesError), changesError].filter((e) => !!e).join(" | ");

  return (
    <ContentSegment>
      <SecondaryMenu>
        <Menu.Item header>
          <span>
            Platform notifications {"  "}
            {!!creatableTargets.length && <Button basic size="tiny" disabled={!!modal} onClick={() => setModal({})} icon="plus" content="Rule" />}
          </span>
        </Menu.Item>
      </SecondaryMenu>
      <ErrorMessage active={!!error} message={error} onDismiss={() => setError(undefined)} />
      <ErrorMessage active={!!loadErrors} header="Failed to load" message={loadErrors} />
      {modal && (
        <CreateNotificationRuleModal
          {...props}
          open
          existing={modal.existing}
          targets={creatableTargets}
          deploymentSummaries={deploymentSummaries}
          organizationSummaries={organizationSummaries}
          onClose={() => setModal(undefined)}
          onSaved={() => {
            reload();
          }}
        />
      )}
      <TabLayout id="platformNotificationsTabs" tabConfig={tabConfig} activeIndex={activeTab} onTabChange={setActiveTab} />
    </ContentSegment>
  );
};

export default withRefresh()(PlatformNotificationsView);
